/*
 * Template model (version 1). All positions and sizes are in millimetres,
 * relative to the printable area (paper minus margins).
 *
 * {
 *   version: 1, docType: "billing_invoice", name: "My invoice",
 *   paper: { preset: "A4" | "A5" | ... | "T80" | "CUSTOM", width, height, orientation, continuous,
 *            margins: { top, right, bottom, left } },
 *   defaults: { fontFamily, fontSize (pt), color },
 *   variables: [ { name: "grand_total", expr: "SUM(col.amount) - order.sot_total_discount" } ],
 *   bands: [
 *     // fixed band: a free canvas of `height` mm, elements positioned absolutely
 *     { id, type: "fixed", name, height, repeat: "none" | "top" | "bottom", visibleIf, bg, elements: [...] },
 *     // table band: grows with the rows of its table (one table per band)
 *     { id, type: "table", name, paddingTop, paddingBottom, visibleIf, elements: [ tableElement ] },
 *   ],
 * }
 *
 * Elements: { id, type, x, y, w, h, visibleIf, style: { fontFamily, fontSize, bold, italic, underline,
 *   color, bg, align, valign, padding, borderWidth, borderColor, borderStyle, borders: "all" | "tb" | "trbl"...,
 *   radius, wrap, clip, lineHeight, uppercase } }
 *   text    { content: "Invoice {{ inv_no }} dated {{ created_date | date:'DD MMM YYYY' }}" }
 *   field   { expr: "order.sot_total_amount * 2", format: "money", prefix, suffix }
 *   image   { src: "https://..." | data URI, srcExpr: "store.company_logo", fit }
 *   line    { orientation: "h" | "v", style: { lineWidth (pt), lineStyle, color } }
 *   rect    {}
 *   qr      { expr, ecl: "L" | "M" | "Q" | "H" }
 *   barcode { expr, showText }
 *   table   { name, source: "items" | "batches" | "payments" | "tax" | "creditNotes", filter, sortBy, sortDir,
 *             x, w, showHeader, repeatHeader, showFooter, minRows,
 *             style: { fontSize, grid: "all" | "rows" | "columns" | "header" | "outer" | "none", borderWidth,
 *                      borderColor, cellPadding, rowHeight, zebra, valign },
 *             headerStyle: { bg, color, bold, fontSize, align }, footerStyle: {...},
 *             columns: [ { id, key, header, expr, width (relative), align, format, nowrap, visibleIf,
 *                          footer: "SUM(col.amount)", footerText, footerFormat, style } ] }
 */

export const TEMPLATE_VERSION = 1;

export const PAPER_PRESETS = [
  { id: "A4", label: "A4", desc: "210 × 297 mm", width: 210, height: 297 },
  { id: "A5", label: "A5", desc: "148 × 210 mm", width: 148, height: 210 },
  { id: "A3", label: "A3", desc: "297 × 420 mm", width: 297, height: 420 },
  { id: "A6", label: "A6", desc: "105 × 148 mm", width: 105, height: 148 },
  { id: "LETTER", label: "Letter", desc: "8.5 × 11 in", width: 215.9, height: 279.4 },
  { id: "LEGAL", label: "Legal", desc: "8.5 × 14 in", width: 215.9, height: 355.6 },
  { id: "T58", label: 'Thermal 2"', desc: "58 mm roll", width: 58, height: 0, continuous: true, thermal: true },
  { id: "T57", label: 'Thermal 2.25"', desc: "57 mm roll", width: 57, height: 0, continuous: true, thermal: true },
  { id: "T76", label: 'Thermal 3"', desc: "76 mm roll", width: 76, height: 0, continuous: true, thermal: true },
  { id: "T80", label: 'Thermal 3.15"', desc: "80 mm roll", width: 80, height: 0, continuous: true, thermal: true },
  { id: "T104", label: 'Thermal 4"', desc: "104 mm roll", width: 104, height: 0, continuous: true, thermal: true },
  { id: "T112", label: 'Thermal 4.4"', desc: "112 mm roll", width: 112, height: 0, continuous: true, thermal: true },
  { id: "CUSTOM", label: "Custom", desc: "Your own size", width: 210, height: 148 },
];

export const PAPER_MAP = PAPER_PRESETS.reduce((m, p) => {
  m[p.id] = p;
  return m;
}, {});

// Thermal heads can't print to the paper edge (80 mm rolls print about 72 mm,
// 58 mm rolls about 48 mm), so side margins keep the content inside that.
export function thermalMargins(width) {
  const side = width <= 58 ? 5 : 4;
  return { top: 2, right: side, bottom: 4, left: side };
}

export function paperFromPreset(id, prev = {}) {
  const p = PAPER_MAP[id] || PAPER_MAP.A4;
  const thermal = !!p.thermal;
  return {
    preset: p.id,
    width: p.id === "CUSTOM" ? prev.width || p.width : p.width,
    height: p.id === "CUSTOM" ? prev.height ?? p.height : p.height,
    orientation: thermal ? "portrait" : prev.orientation || "portrait",
    continuous: p.id === "CUSTOM" ? !!prev.continuous : !!p.continuous,
    margins: thermal ? thermalMargins(p.width) : prev.margins && !PAPER_MAP[prev.preset]?.thermal ? prev.margins : { top: 8, right: 8, bottom: 8, left: 8 },
  };
}

export const FONT_FAMILIES = ["Arial", "Helvetica", "Verdana", "Tahoma", "Calibri", "Segoe UI", "Roboto", "Times New Roman", "Georgia", "Courier New", "Consolas", "Monospace"];

export const FORMAT_OPTIONS = [
  { id: "", label: "As is" },
  { id: "money", label: "Amount 1,23,456.00" },
  { id: "currency", label: "₹ 1,23,456.00" },
  { id: "fixed:2", label: "Number 0.00" },
  { id: "fixed:3", label: "Number 0.000" },
  { id: "int", label: "Whole number" },
  { id: "percent", label: "Percent 12%" },
  { id: "words:after", label: "In words: Ten Rupees Only" },
  { id: "words", label: "In words: Rupees Ten Only" },
  { id: "date:DD/MM/YYYY", label: "Date 16/10/2026" },
  { id: "date:DD-MM-YYYY", label: "Date 16-10-2026" },
  { id: "date:DD MMM YYYY", label: "Date 16 Oct 2026" },
  { id: "date:MM/YY", label: "Expiry 10/26" },
  { id: "datetime", label: "Date & time" },
  { id: "upper", label: "UPPERCASE" },
  { id: "title", label: "Title Case" },
  { id: "dash", label: "Blank / zero → -" },
  { id: "blankzero", label: "Hide zero" },
];

let counter = 0;
export function uid(prefix = "e") {
  counter = (counter + 1) % 1e6;
  return `${prefix}${Date.now().toString(36)}${counter.toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

export function defaultStyle(overrides = {}) {
  return { fontSize: 9, align: "left", valign: "top", ...overrides };
}

export function newElement(type, { x = 2, y = 2 } = {}, extra = {}) {
  const base = { id: uid(type.slice(0, 2)), type, x, y, w: 40, h: 6, style: defaultStyle() };
  switch (type) {
    case "text":
      return { ...base, content: "Double-click to edit", ...extra };
    case "field":
      return { ...base, expr: extra.expr || "", format: extra.format || "", ...extra };
    case "image":
      return { ...base, w: 30, h: 15, src: "", srcExpr: "", fit: "contain", placeholder: "Image", ...extra };
    case "line":
      return { ...base, w: 60, h: 2, orientation: "h", style: { lineWidth: 0.75, color: "#000000", lineStyle: "solid" }, ...extra };
    case "rect":
      return { ...base, w: 40, h: 20, style: { borderWidth: 0.75, borderColor: "#000000", bg: "" }, ...extra };
    case "qr":
      return { ...base, w: 22, h: 22, expr: extra.expr || 'order.inv_no', ecl: "M", ...extra };
    case "barcode":
      return { ...base, w: 50, h: 14, expr: extra.expr || 'order.inv_no', showText: true, style: defaultStyle({ fontSize: 7 }), ...extra };
    default:
      return { ...base, ...extra };
  }
}

export function newColumn(extra = {}) {
  return { id: uid("c"), key: extra.key || `col${Math.floor(Math.random() * 1000)}`, header: "Column", expr: "", width: 10, align: "left", format: "", ...extra };
}

export function newTable(width, extra = {}) {
  return {
    id: uid("tb"),
    type: "table",
    name: "items",
    source: "items",
    x: 0,
    w: width,
    showHeader: true,
    repeatHeader: true,
    showFooter: false,
    minRows: 0,
    filter: "",
    sortBy: "",
    sortDir: "asc",
    style: { fontSize: 8, grid: "all", borderWidth: 0.5, borderColor: "#000000", cellPadding: 1, valign: "top" },
    headerStyle: { bold: true, bg: "#f1f5f9" },
    footerStyle: { bold: true },
    columns: [
      newColumn({ key: "sno", header: "#", expr: "SNO()", width: 5, align: "center" }),
      newColumn({ key: "name", header: "Item", expr: "item_name", width: 40 }),
      newColumn({ key: "qty", header: "Qty", expr: "item_quantity", width: 10, align: "right", footer: "SUM(col.qty)" }),
      newColumn({ key: "rate", header: "Rate", expr: "item_unit_price", width: 15, align: "right", format: "money" }),
      newColumn({ key: "amount", header: "Amount", expr: "col.qty * col.rate", width: 15, align: "right", format: "money", footer: "SUM(col.amount)" }),
    ],
    ...extra,
  };
}

export function newBand(type, contentWidth, extra = {}) {
  if (type === "table") {
    return { id: uid("bd"), type: "table", name: "Items table", paddingTop: 1, paddingBottom: 1, elements: [newTable(contentWidth)], ...extra };
  }
  return { id: uid("bd"), type: "fixed", name: "Section", height: 25, repeat: "none", elements: [], ...extra };
}

export function newTemplate(docType, presetId = "A4") {
  const paper = paperFromPreset(presetId);
  const cw = (paper.width || 210) - paper.margins.left - paper.margins.right;
  return {
    version: TEMPLATE_VERSION,
    docType,
    name: "Untitled format",
    paper,
    defaults: { fontFamily: "Arial", fontSize: 9, color: "#000000" },
    variables: [],
    bands: [
      newBand("fixed", cw, { name: "Header", height: 30 }),
      newBand("table", cw),
      newBand("fixed", cw, { name: "Totals", height: 25 }),
    ],
  };
}

// Normalize/upgrade anything loaded from the server or a file.
export function normalizeTemplate(t) {
  const out = { ...t };
  out.version = TEMPLATE_VERSION;
  out.paper = { ...paperFromPreset(out.paper?.preset || "A4", out.paper || {}), ...(out.paper || {}) };
  out.paper.margins = { top: 8, right: 8, bottom: 8, left: 8, ...(out.paper.margins || {}) };
  out.defaults = { fontFamily: "Arial", fontSize: 9, color: "#000000", ...(out.defaults || {}) };
  out.variables = Array.isArray(out.variables) ? out.variables : [];
  out.bands = (Array.isArray(out.bands) ? out.bands : []).map((b) => ({ ...b, id: b.id || uid("bd"), elements: (b.elements || []).map((e) => ({ ...e, id: e.id || uid("e") })) }));
  return out;
}

// Deep clone with fresh ids (used for duplicate / paste).
export function cloneWithNewIds(obj) {
  const c = JSON.parse(JSON.stringify(obj));
  const walk = (o) => {
    if (!o || typeof o !== "object") return;
    if (Array.isArray(o)) return o.forEach(walk);
    if (o.id && (o.type || o.key !== undefined)) o.id = uid(o.type ? o.type.slice(0, 2) : "c");
    Object.values(o).forEach(walk);
  };
  walk(c);
  return c;
}
